/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EvidencePart } from "@interfaces/Interfaces";
import { Text, View } from "@react-pdf/renderer";
import Textarea from "@mui/joy/Textarea";
import classNames from "classnames";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import Autocomplete from "@mui/joy/Autocomplete";
import {
  findSupervisorEmailValueByInput,
  supervisorAutofillSelectionAtom,
  supervisorEmailsFromRoleAtom,
} from "@state/EvidenceState";
import { useRecoilValue, useSetRecoilState } from "recoil";

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  onValueChanged?: (value: any, isInput?: boolean) => void;
  onInputBlurred: () => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  isLocked?: boolean;
  pdfStyles: any;
  initialValue?: any;
  id: string;
  multiline: boolean;
};

/** PDF/display rendering runs outside RecoilRoot (@react-pdf/renderer); must not use Recoil hooks. */
const TextBoxStatic: React.FC<Props> = (props) => {
  if (props.definition["Display Title"] === "Title") {
    return null;
  }

  if (props.isPDF) {
    return (
      <View>
        <Text style={props.pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(props.definition)}</Text>
        <Text style={props.pdfStyles.evidenceDate}>{props.data}</Text>
      </View>
    );
  }

  return (
    <div className="readEvidenceBlock">
      <div className="readEvidenceHeader">{props.definition["Display Title"] || props.definition.Name}</div>
      <div className={`readEvidenceText ${props.multiline && "whitespace-pre-wrap"}`}>{props.data}</div>
      <div className="mt-4 h-[1px] w-full bg-grey-30" />
    </div>
  );
};

const TextBoxInteractive: React.FC<Props> = (props) => {
  const [value, setValue] = useState<string>(props.initialValue || "");
  const [initialValueSet, setInitialValueSet] = useState<boolean>(false);
  const supervisorEmails = useRecoilValue(supervisorEmailsFromRoleAtom);
  const supervisorSelection = useRecoilValue(supervisorAutofillSelectionAtom);
  const setSupervisorAutofillSelection = useSetRecoilState(supervisorAutofillSelectionAtom);

  const supervisorNameOptions = useMemo(
    () => supervisorEmails?.map((email) => email.name).sort() ?? [],
    [supervisorEmails]
  );

  const publishSupervisorSelectionForInput = useCallback(
    (input: string) => {
      const match = findSupervisorEmailValueByInput(supervisorEmails, input);
      setSupervisorAutofillSelection(match);
    },
    [supervisorEmails, setSupervisorAutofillSelection]
  );

  const hasSubtitle = useMemo(() => {
    return !!props.definition?.["Display Subtitle"];
  }, [props.definition]);

  useEffect(() => {
    if (props.isLocked && !initialValueSet) {
      setValue(props.initialValue);
      setInitialValueSet(true);
      props.onValueChanged && props.onValueChanged(props.initialValue, true);
    }
  }, [props.initialValue, initialValueSet, props.isLocked, props.onValueChanged]);

  useEffect(() => {
    if (!props.initialValue?.length) {
      setValue("");
    }
  }, [props.id]);

  /** When another supervisor-autofill field picks a row (`SupervisorEmailValue.key`), mirror this field to that row's name. */
  useEffect(() => {
    if (!props.definition.EnableSupervisorAutofill || !supervisorSelection) {
      return;
    }
    const desired = supervisorSelection.name;
    if (desired !== value) {
      setValue(desired);
      props.onValueChanged && props.onValueChanged(desired, true);
    }
  }, [supervisorSelection?.key, props.definition.EnableSupervisorAutofill]);

  const autocompleteChanged = (_event: React.SyntheticEvent | null, newInputValue: string): void => {
    const _value = newInputValue ?? "";

    setValue(_value);
    props.onValueChanged && props.onValueChanged(_value, true);
    publishSupervisorSelectionForInput(_value);
  };

  const inputChanged = (event: any): void => {
    const _value = (event.target as HTMLTextAreaElement).value;

    setValue(_value);
    props.onValueChanged && props.onValueChanged(_value, true);
  };

  const inputBlurred = (): void => {
    props.onInputBlurred && props.onInputBlurred();
  };

  if (props.definition.EnableSupervisorAutofill) {
    return (
      <Autocomplete
        options={supervisorNameOptions}
        freeSolo
        placeholder={props.definition?.["Placeholder Text"] ?? "Enter a value..."}
        autoComplete={false}
        value={value}
        getOptionLabel={(option) => String(option ?? "")}
        onInputChange={autocompleteChanged}
        onBlur={inputBlurred}
        variant="plain"
        sx={{
          "--Textarea-placeholderOpacity": 1,
          backgroundColor: "white",
          minHeight: "28px",
          color: value.length ? "black" : "var(--grey-60)", // different color for placeholder text
          paddingLeft: 0,
          fontSize: "20px",
          fontWeight: "600",
          "@media (max-width: 500px)": {
            fontSize: "17px",
          },
          ":hover": {
            color: value.length ? "black" : "var(--grey-60)", // different color for placeholder text
          },
        }}
      />
    );
  }

  return (
    <div className={classNames({ "mt-2": hasSubtitle })}>
      {props.multiline ? (
        <Textarea
          placeholder={props.definition?.["Placeholder Text"] || "Enter a value..."}
          disabled={props.isLocked}
          value={value}
          onChange={(e) => inputChanged(e)}
          variant="plain"
          sx={{
            "--Textarea-placeholderOpacity": 1,
            backgroundColor: "white",
            minHeight: "28px",
            color: value.length ? "black" : "var(--grey-60)", // different color for placeholder text
            padding: 0,
            fontSize: "20px",
            fontWeight: "600",
            "@media (max-width: 500px)": {
              fontSize: "17px",
            },
            ":hover": {
              color: value.length ? "black" : "var(--grey-60)", // different color for placeholder text
            },
          }}
        />
      ) : (
        <input
          className="reflectionTagsInput -ml-0.5"
          placeholder={props.definition?.["Placeholder Text"] || "Enter a value..."}
          autoComplete="off"
          disabled={props.isLocked}
          value={value}
          onChange={inputChanged}
          onBlur={inputBlurred}
          maxLength={props.definition["Max value"] ?? undefined}
        />
      )}
    </div>
  );
};

const TextBox: React.FC<Props> = (props) => {
  if (props.isPDF || props.isDisplay) {
    return <TextBoxStatic {...props} />;
  }
  return <TextBoxInteractive {...props} />;
};

export default TextBox;
